import type { Ref } from 'vue';

import { useApolloClient, useMutation } from '@vue/apollo-composable';
import { computed, ref, toValue } from 'vue';

import { useAuthStore } from '@/entities/auth';
import { useFiscalWorkstationStorage } from '@/entities/fiscalization';
import { apolloOperationGate, getKkmServerAdapter } from '@/shared/api';
import { requirePayload } from '@/shared/lib';
import {
  AnswerOrderFiscalRefundReceiptPrintedDocument,
  CompleteOrderFiscalRefundDocument,
  FailOrderFiscalRefundDocument,
  GetOrderFiscalRefundOperationDocument,
  type OrderFieldsFragment,
  PrepareOrderFiscalRefundDocument,
  type PrepareOrderFiscalRefundInput,
  ResolveOrderFiscalRefundReceiptDocument,
  RetryOrderFiscalRefundReceiptDocument,
  StartOrderFiscalRefundDocument,
} from '@shared/api/graphql/gql/graphql';

import type { OrderFiscalizationPhase, OrderFiscalizationState } from './order-fiscalization-state';
import type { OrderFiscalRecoverOptions } from './order-fiscalization-types';

import { requestCashShiftUi } from './cash-shift-ui-events';
import { FISCAL_MUTATION_OPTIONS, createFiscalPaymentMutationOptions } from './fiscal-mutation-options';
import { OrderFiscalRefundController } from './order-fiscal-refund-controller';
import { BUSY_PHASES, createInitialOrderFiscalizationState } from './order-fiscalization-state';

export function useOrderFiscalRefund(options: {
  /** Привязанная ККТ локации (`orderFiscalOperationalStatus.expected_kkt_device_id`). */
  expectedKktDeviceId?: Ref<string | null | undefined>;
  locationId: Ref<string | null | undefined>;
  organizationId: Ref<string | null | undefined>;
}) {
  const { resolveClient } = useApolloClient();
  const adapter = getKkmServerAdapter();
  const authStore = useAuthStore();
  const state = ref<OrderFiscalizationState>(createInitialOrderFiscalizationState());
  const paymentMutationOptions = createFiscalPaymentMutationOptions();
  const { mutate: prepare } = useMutation(PrepareOrderFiscalRefundDocument, FISCAL_MUTATION_OPTIONS);
  const { mutate: start } = useMutation(StartOrderFiscalRefundDocument, FISCAL_MUTATION_OPTIONS);
  const { mutate: complete } = useMutation(CompleteOrderFiscalRefundDocument, paymentMutationOptions);
  const { mutate: fail } = useMutation(FailOrderFiscalRefundDocument, paymentMutationOptions);
  const { mutate: retryReceipt } = useMutation(RetryOrderFiscalRefundReceiptDocument, FISCAL_MUTATION_OPTIONS);
  const { mutate: resolveReceipt } = useMutation(ResolveOrderFiscalRefundReceiptDocument, paymentMutationOptions);
  const { mutate: answerReceiptPrinted } = useMutation(
    AnswerOrderFiscalRefundReceiptPrintedDocument,
    paymentMutationOptions,
  );
  let controller: OrderFiscalRefundController | null = null;
  let scope = '';

  const currentController = (): OrderFiscalRefundController => {
    const organizationId = toValue(options.organizationId);
    const locationId = toValue(options.locationId);
    if (!organizationId || !locationId) throw new Error('Выберите организацию и локацию возврата.');
    const nextScope = `${organizationId}:${locationId}`;
    if (controller !== null && scope === nextScope) return controller;

    const storage = useFiscalWorkstationStorage(organizationId, locationId);
    scope = nextScope;
    controller = new OrderFiscalRefundController({
      adapter,
      timezone: () => (authStore.currentLocation?.id === locationId ? authStore.currentLocation.timezone : undefined),
      expectedKktDeviceId: () => toValue(options.expectedKktDeviceId),
      onState: (next) => {
        state.value = { ...next };
      },
      services: {
        answerReceiptPrinted: async (input) =>
          requirePayload(
            (await answerReceiptPrinted({ input }))?.data?.answerOrderFiscalReceiptPrinted.execution,
            'Mors не сохранил ответ о чеке.',
          ),
        complete: async (input) =>
          requirePayload(
            (await complete({ input }))?.data?.completeOrderFiscalRefund,
            'Mors не подтвердил результат возврата.',
          ),
        fail: async (input) =>
          requirePayload((await fail({ input }))?.data?.failOrderFiscalRefund, 'Mors не зафиксировал ошибку возврата.'),
        find: async (orderId) => {
          const response = await resolveClient().query({
            fetchPolicy: 'network-only',
            query: GetOrderFiscalRefundOperationDocument,
            variables: { orderId },
          });
          return response.data.orderFiscalRefundOperation ?? null;
        },
        prepare: async (input) =>
          requirePayload((await prepare({ input }))?.data?.prepareOrderFiscalRefund, 'Mors не подготовил возврат.'),
        resolveReceipt: async (input) =>
          requirePayload(
            (await resolveReceipt({ input }))?.data?.resolveOrderFiscalReceipt.execution,
            'Mors не принял документы кассы.',
          ),
        retryReceipt: async (input) =>
          requirePayload(
            (await retryReceipt({ input }))?.data?.retryOrderFiscalReceipt,
            'Mors не разрешил повтор печати чека.',
          ),
        start: async (input) =>
          requirePayload((await start({ input }))?.data?.startOrderFiscalRefund, 'Mors не запустил возврат.'),
      },
      storage,
    });
    return controller;
  };

  // Как у оплаты: второй клик, пока возврат ждёт очереди шлюза, не встаёт в очередь.
  let refunding = false;
  const refund = async (
    input: Omit<PrepareOrderFiscalRefundInput, 'browserWorkstationId' | 'kktDeviceId' | 'kkt' | 'terminal'>,
  ): Promise<OrderFieldsFragment | null> => {
    if (refunding) return null;
    refunding = true;
    try {
      return await apolloOperationGate.runExclusive('fiscalization', () => currentController().refund(input));
    } finally {
      refunding = false;
    }
  };
  const recover = (orderId: string, recoverOptions?: OrderFiscalRecoverOptions): Promise<OrderFieldsFragment | null> =>
    apolloOperationGate.runExclusive('fiscalization', () => currentController().recover(orderId, recoverOptions));
  /** Цикл смены без шлюза: действие смены уже держит `apolloOperationGate`. Возвращает итоговую фазу. */
  const recoverInShiftCycle = async (orderId: string): Promise<OrderFiscalizationPhase> => {
    const current = currentController();
    await current.recover(orderId, { startPayment: false });

    return current.state.phase;
  };
  const retryReceiptAction = (): Promise<OrderFieldsFragment | null> =>
    apolloOperationGate.runExclusive('fiscalization', () => currentController().retryReceipt());
  const resolveReceiptAction = (): Promise<OrderFieldsFragment | null> =>
    apolloOperationGate.runExclusive('fiscalization', () => currentController().resolveReceipt());
  const answerReceiptPrintedAction = (printed: boolean): Promise<OrderFieldsFragment | null> =>
    apolloOperationGate.runExclusive('fiscalization', () => currentController().answerReceiptPrinted(printed));
  const retryFinalize = (): Promise<OrderFieldsFragment | null> =>
    apolloOperationGate.runExclusive('fiscalization', () => currentController().retryFinalize());
  const reset = (): boolean => currentController().reset();
  const fixShift = (): Promise<boolean> =>
    Promise.resolve(state.value.shiftAction === null ? false : requestCashShiftUi(state.value.shiftAction));
  const isBusy = computed(() => BUSY_PHASES.has(state.value.phase));
  // `printed_pending_sync` держит fiscal payload только в памяти контроллера.
  const isBlocking = computed(
    () =>
      isBusy.value ||
      ['payment_without_receipt', 'print_result_unknown', 'printed_pending_sync'].includes(state.value.phase),
  );

  return {
    answerReceiptPrinted: answerReceiptPrintedAction,
    fixShift,
    isBlocking,
    isBusy,
    recover,
    recoverInShiftCycle,
    refund,
    reset,
    resolveReceipt: resolveReceiptAction,
    retryFinalize,
    retryReceipt: retryReceiptAction,
    state,
  };
}
